import { useEffect, useRef } from 'react';
import { updateAdjudicationTime } from '../../../actions/adjudication';
import { connect } from "react-redux";

/**
 * Tracks time spent on adjudication and sends updates after 20 seconds of inactivity
 * @param claimID
 * @param claimStatus
 * @param isPendingStatusChange
 * @param dispatch
 */
const AdjudicationTimeTracker = ({ claimID, claimStatus, isPendingStatusChange = false, dispatch }) => {
    // Use refs instead of state to avoid re-renders on every activity
    const idleTimeoutRef = useRef(null);
    const startTimeRef = useRef(Date.now()); // Tracks when current active session started
    const isIdleRef = useRef(false); // Tracks if user is currently idle

    /**
     * Send a final adjudication time update if the status changes from pending to something else (approved or declined)
     */
    const sendFinalAdjudicationTimeUpdate = async () => {
        const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
        await dispatch(updateAdjudicationTime(claimID, timeSpent));

        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current);
        }
    };

    useEffect(() => {
        // If claim is being submitted to the server for adjudication, send final update
        if (isPendingStatusChange && claimStatus?.toLowerCase() === 'pending') {
            sendFinalAdjudicationTimeUpdate();
        }
    }, [ isPendingStatusChange ]);

    useEffect(() => {
        if (!claimID || claimStatus?.toLowerCase() !== 'pending') {
            return null;
        }

        /**
         * Resets the idle timeout when user is active
         * - Clears the existing 20-second timeout
         * - Starts a new 20-second countdown
         * - If returning from idle, resets the start time
         */
        const resetIdleTimeout = () => {
            // User was idle but is now active - start a fresh session
            if (isIdleRef.current) {
                startTimeRef.current = Date.now();
                isIdleRef.current = false;
            }

            // Clear existing timeout
            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current);
            }

            // Start new 20 second idle countdown
            idleTimeoutRef.current = setTimeout(async () => {
                // Calculate time spent in this active session
                const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
                await dispatch(updateAdjudicationTime(claimID, timeSpent));
                
                // Mark as idle - next activity will start a new session
                isIdleRef.current = true;
            }, 20000);
        };

        // Listen for user activity
        window.addEventListener('keydown', resetIdleTimeout);
        window.addEventListener('mousedown', resetIdleTimeout);
        window.addEventListener('mousemove', resetIdleTimeout);
        window.addEventListener('wheel', resetIdleTimeout);
        resetIdleTimeout();

        // Cleanup
        return () => {
            window.removeEventListener('keydown', resetIdleTimeout);
            window.removeEventListener('mousedown', resetIdleTimeout);
            window.removeEventListener('mousemove', resetIdleTimeout);
            window.removeEventListener('wheel', resetIdleTimeout);
            
            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current);
            }
        };
    }, [ claimID, claimStatus ]);
                      
    return null;
};

export default connect()(AdjudicationTimeTracker);